import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Tag, Clock, Search, RefreshCw, Bell, Loader2 } from "lucide-react";
import client, { money } from "@/lib/api";
import { DealCard, addToList } from "@/pages/Home";
import PageHero from "@/components/PageHero";
import CatalogBrowser from "@/components/CatalogBrowser";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const Row = ({ title, icon: Icon, items, testId }) => (
  <section data-testid={testId}>
    <h2 className="flex items-center gap-2 text-lg font-extrabold text-pp-text mb-3 capitalize"><Icon className="h-5 w-5 text-pp-forest" /> {title}</h2>
    <div className="flex gap-3 overflow-x-auto snap-x py-1 pb-3 scrollbar-none">
      {items.map((d, i) => <DealCard key={d.id || i} d={d} testId={`${testId}-card-${i}`} onAdd={addToList} />)}
    </div>
  </section>
);

const toDeal = (p) => ({ id: p.id, name: p.product_name, merchant: p.store_name, price: p.sale_price, image: p.image,
  was_text: p.regular_price ? `was ${money(p.regular_price)} · -${p.discount_pct}%` : "" });

export default function Discover() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [deals, setDeals] = useState(null);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [plan, setPlan] = useState(null);

  const loadDeals = useCallback(() => { setDeals(null); client.get("/live/deals").then((r) => setDeals(r.data)).catch(() => setDeals({ sections: [] })); }, []);
  const runSearch = useCallback(async (term) => {
    if (!term || term.trim().length < 2) { setResults(null); return; }
    setSearching(true);
    try { const { data } = await client.get(`/live/flyer?q=${encodeURIComponent(term.trim())}`); setResults(data.items); }
    catch { toast.error("Search failed"); } finally { setSearching(false); }
  }, []);

  useEffect(() => {
    loadDeals();
    client.get("/discover").then((r) => setPlan(r.data)).catch(() => setPlan({}));
  }, [loadDeals]);
  useEffect(() => { runSearch(params.get("q")); }, [params, runSearch]);

  const submit = (e) => { e.preventDefault(); setParams(q.trim() ? { q: q.trim() } : {}); };
  const alertMe = async () => {
    try { await client.post("/alerts", { product_name: q.trim(), type: "on_sale" }); toast.success(`We'll alert you when ${q.trim()} goes on sale`); }
    catch { toast.error("Could not create alert"); }
  };

  return (
    <div className="space-y-7">
      <PageHero image="/img/crates.jpg" eyebrow="Step 3 · Shop" title="Shop every product. Pay the lowest price." subtitle="Name brands, store brands and this week's deals — compared across stores near you." testId="discover-hero" />

      <Tabs defaultValue={params.get("q") ? "deals" : "all"}>
        <TabsList className="rounded-full bg-white pp-shadow p-1 h-auto">
          <TabsTrigger value="all" data-testid="tab-all-products" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-pp-forest data-[state=active]:text-white">All products</TabsTrigger>
          <TabsTrigger value="deals" data-testid="tab-deals" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-pp-forest data-[state=active]:text-white">Flyer deals</TabsTrigger>
        </TabsList>
        <TabsContent value="all" className="mt-5"><CatalogBrowser /></TabsContent>
        <TabsContent value="deals" className="mt-5 space-y-7">
        <form onSubmit={submit} className="relative max-w-lg">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-pp-muted" />
          <Input data-testid="discover-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search this week's flyers"
            className="h-12 pl-12 pr-28 rounded-2xl bg-white text-pp-text border-0 pp-shadow" />
          <Button type="submit" data-testid="discover-search-button" className="absolute right-1.5 top-1.5 h-9 rounded-xl bg-pp-forest">Search</Button>
        </form>

      {searching && <div className="grid place-items-center py-6"><Loader2 className="h-6 w-6 animate-spin text-pp-forest" /></div>}
      {results && !searching && (
        <section data-testid="discover-results">
          <div className="flex items-center justify-between mb-3 gap-3">
            <h2 className="text-lg font-extrabold text-pp-text">{results.length} flyer results for “{params.get("q")}”</h2>
            <Button variant="outline" size="sm" onClick={alertMe} data-testid="discover-alert-me" className="rounded-full bg-white"><Bell className="h-4 w-4 mr-1" /> Alert me</Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {results.map((d, i) => <div key={d.id} className="[&>div]:w-full"><DealCard d={d} testId={`discover-result-${i}`} onAdd={addToList} /></div>)}
          </div>
          {!results.length && <p className="text-sm text-pp-muted">Nothing on flyers right now — set an alert and we'll tell you when it's on sale.</p>}
        </section>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-pp-muted">A fresh mix of categories every visit</p>
        <button onClick={loadDeals} data-testid="discover-shuffle" className="flex items-center gap-1 text-sm font-semibold text-pp-forest hover:underline"><RefreshCw className="h-4 w-4" /> Shuffle</button>
      </div>
      {!deals ? [...Array(2)].map((_, i) => <Skeleton key={i} className="h-60 rounded-3xl" />)
        : deals.sections.map((s) => <Row key={s.term} title={`${s.term} deals`} icon={Tag} items={s.items} testId={`discover-section-${s.term.replace(/\s/g, "-")}`} />)}

      {plan?.ending_soon?.length > 0 && <Row title="Ending soon at stores we compare" icon={Clock} items={plan.ending_soon.map(toDeal)} testId="discover-ending-soon" />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
